import React, { useState } from 'react';
import { useCombobox } from 'downshift';
import { COUNTRY_LIST } from './CountryList';
import type { Country } from './CountryList';
import type { FieldValues, UseFormRegister } from 'react-hook-form';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import classNames from 'classnames';
import styles from '../../atoms/primary-dropdown/PrimaryDropdown.module.scss';
import PrimaryLink from '../../atoms/primary-link/PrimaryLink';
import type { Option } from '../../blocks/pardot-pdf-form/PardotPdfForm.data';
import debounce from 'lodash/debounce';

type CountryDropdownProps = {
  register?: UseFormRegister<FieldValues>;
  name?: string | null | undefined;
  defaultProps?: Partial<{
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    menuStyle?: React.CSSProperties;
  }>;
  menuClassName?: string;
  index?: number;
};

const CountryDropdown: React.FC<CountryDropdownProps> = ({ defaultProps = {}, ...props }) => {
  const [inputValue, setInputValue] = useState('');

  const filteredItems = COUNTRY_LIST.filter(
    (country: Country) =>
      country.alias?.toLowerCase().includes(inputValue.toLowerCase()) ||
      country.value.toLowerCase().includes(inputValue.toLowerCase()),
  );

  const { lock, unLock } = useScrollProviderContext() as ScrollProviderContextType;

  const { isOpen, getMenuProps, getInputProps, getItemProps, highlightedIndex, selectItem } =
    useCombobox<Option>({
      items: filteredItems,
      itemToString: (item) => item?.value || '',
      inputValue,
      onInputValueChange: ({ inputValue }) => {
        setInputValue(inputValue || '');
      },
      onSelectedItemChange: ({ selectedItem }) => {
        unLock();
        if (selectedItem) {
          setInputValue(selectedItem.value);
          selectItem(selectedItem);
        } else {
          setInputValue('');
        }
      },
    });

  const debounceUnlock = debounce(unLock, 150);

  return (
    <>
      <div
        className={classNames(styles.dropdownWrapper)}
        style={{
          width: '100%',
          position: 'relative',
          zIndex: 10000000 - (props.index || 0),
        }}
      >
        <input
          {...getInputProps({
            ...defaultProps.inputProps,
            ...props?.register?.(props.name || ''),
            onBlur: (e) => {
              if (filteredItems.length > 0 && inputValue) {
                setInputValue(filteredItems[0].value);
                selectItem(filteredItems[0]);
              } else {
                setInputValue('');
                selectItem(null);
              }
              if (props.register?.(props.name || '').onBlur) {
                props.register(props.name || '').onBlur(e);
              }
              unLock();
            },
          })}
          className={classNames(styles.dropdownInput, defaultProps.inputProps?.className)}
        />

        <ul
          {...getMenuProps({
            style: {
              ...defaultProps.menuStyle,
            },
            className: classNames(
              styles.customDropdown,
              props.menuClassName,
              isOpen && styles.isOpen,
            ),
          })}
          onScroll={() => {
            lock();
            debounceUnlock();
          }}
        >
          {isOpen &&
            filteredItems.map((item, index) => (
              <li
                key={item.key}
                {...getItemProps({ item, index })}
                className={classNames(styles.listItem)}
              >
                <PrimaryLink
                  textVariant="body-standard"
                  className={classNames(styles.button, {
                    [styles.isHighlighted]: highlightedIndex === index,
                  })}
                  label={item.value}
                />
              </li>
            ))}
        </ul>
      </div>
    </>
  );
};

export default CountryDropdown;
